import { resolveFlyPayReturnRouting } from "./flypayReturnRouting.js";
import { validateCheckoutPhone } from "./internationalPhone.js";

export const FLYPAY_PAY_TYPE = 1900;
export const FLYPAY_CURRENCY = "AUD";
const FLYPAY_RETURN_SITE_PARAM = "auFlypayReturn";

function usesFlyPayProductionOrigin(origin, apiEnvironment, routing) {
  if (String(apiEnvironment || "").trim().toLowerCase() === "prod") return true;
  // Deployed builds also use the default "test" development-environment flag.
  // Only an explicitly configured checkout source can enable its bridge.
  try { return routing.bridges.some(rule => new URL(origin).origin === rule.sourceOrigin); } catch { return false; }
}

export function isFlyPayPaymentMethod(method = {}) {
  return Number(method.payType ?? method.pay_type) === FLYPAY_PAY_TYPE;
}

export function buildFlyPayPaymentMethods(rawMethods = [], templateKey = "") {
  if (String(templateKey).trim().toLowerCase() !== "australia") return [];

  return rawMethods.filter(isFlyPayPaymentMethod).filter((method) => {
    const active = String(method.activeFlag ?? method.active_flag ?? true).trim().toLowerCase();
    const currency = String(method.payCurrencyCode || method.pay_currency_code || FLYPAY_CURRENCY).trim().toUpperCase();
    return !["false", "0"].includes(active) && currency === FLYPAY_CURRENCY;
  }).map((method) => {
    const methodCode = String(method.methodCode ?? method.paymentMethodCode ?? "").trim();
    return {
      ...method,
      code: methodCode ? `FLYPAY:${methodCode.toUpperCase()}` : "FLYPAY",
      paymentMethodCode: methodCode,
      payType: FLYPAY_PAY_TYPE,
      provider: "flypay",
      name: "FlyPay",
      displayName: "FlyPay",
      currency: FLYPAY_CURRENCY,
      returnType: "element",
      iconUrl: "",
      isCard: false,
      isWallet: true,
    };
  });
}

export function resolveFlyPayOrderCode(data = {}) {
  return String(data.orderCode || data.orderBackDTOList?.[0]?.orderCode || data.mallOrderDTOList?.[0]?.orderCode || "").trim();
}

export function getFlyPayShippingValidationError(address = {}, { requireEmail = true } = {}) {
  const value = buildFlyPayAddress(address);
  if (!value.first_name || !value.last_name) return "payment.enterName";
  if (!validateCheckoutPhone(value.phone)) {
    return !value.phone || value.phone.startsWith("61") ? "payment.invalidAuPhone" : "payment.invalidInternationalPhone";
  }
  if (!value.state || !value.city || !value.address_line) return "payment.fillCompleteAddress";
  if (!/^\d{4}$/.test(value.zipcode)) return "payment.enterPostalCode";
  if (requireEmail && !value.email) return "payment.enterEmailAddress";
  if (value.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email)) return "payment.invalidEmailAddress";
  return "";
}

export function buildFlyPayReturnUrls({ origin, apiEnvironment = "test", orderCode = "", payOrderCode = "", returnRouting }) {
  const routing = resolveFlyPayReturnRouting(returnRouting);
  const production = usesFlyPayProductionOrigin(origin, apiEnvironment, routing);
  const callbackOrigin = production
    ? routing.callbackOrigin
    : origin;
  // Keep the approved production callback URL; its marked bridge selects the
  // final storefront page. Local/test payments can open the success page directly.
  const returnUrl = new URL(production ? "/order/query" : "/payment/successful", callbackOrigin);
  if (orderCode) returnUrl.searchParams.set("orderCode", String(orderCode));
  if (payOrderCode) returnUrl.searchParams.set("payOrderCode", String(payOrderCode));
  returnUrl.searchParams.set("paymentProvider", "flypay");
  returnUrl.searchParams.set("currency", FLYPAY_CURRENCY);
  const bridge = production && routing.bridges.find(rule => rule.sourceOrigin === new URL(origin).origin);
  if (bridge) returnUrl.searchParams.set(FLYPAY_RETURN_SITE_PARAM, bridge.id);
  const quitUrl = new URL(returnUrl);
  quitUrl.pathname = "/order/query";
  quitUrl.searchParams.set("cancelled", "1");
  return { returnPageUrl: returnUrl.toString(), quitUrl: quitUrl.toString() };
}

// SDK completion is already running in the shopper's browser. Go straight to
// the bridge's final destination; only gateway-managed returns need to visit
// the approved callback host generated by buildFlyPayReturnUrls.
export function resolveFlyPayCompletionUrl(options) {
  const { returnPageUrl } = buildFlyPayReturnUrls(options);
  return resolveFlyPayReturnBridgeUrl(returnPageUrl, options.returnRouting) || returnPageUrl;
}

export function resolveFlyPayReturnBridgeUrl(currentUrl, returnRouting) {
  let source;
  try { source = new URL(currentUrl); } catch { return ""; }
  const params = source.searchParams;
  if (!["/order/query", "/order/query/"].includes(source.pathname)
    || !params.has(FLYPAY_RETURN_SITE_PARAM)
    || params.get("paymentProvider") !== "flypay"
    || params.get("currency") !== FLYPAY_CURRENCY) return "";
  const routing = resolveFlyPayReturnRouting(returnRouting);
  const bridge = routing.bridges.find(rule => rule.id === params.get(FLYPAY_RETURN_SITE_PARAM));
  if (source.origin !== routing.callbackOrigin || !bridge) return "";

  // The first checkout has no local order yet; FlyPay can return its reserved
  // payment code as invoice_id/order_no. Never read another site's localStorage.
  const query = Object.fromEntries(
    ["orderCode", "payOrderCode", "merchantOrderNo", "payOrderNo", "invoice_id", "order_no"]
      .map(key => [key, params.get(key)]),
  );
  const { orderCode, payOrderCode } = resolveFlyPayReturnIdentifiers(query);
  const cancelled = params.get("cancelled") === "1";
  const target = new URL(cancelled || (!orderCode && !payOrderCode) ? "/order/query" : "/payment/successful", bridge.targetOrigin);
  if (orderCode) target.searchParams.set("orderCode", orderCode);
  if (payOrderCode) target.searchParams.set("payOrderCode", payOrderCode);
  target.searchParams.set("paymentProvider", "flypay");
  target.searchParams.set("currency", FLYPAY_CURRENCY);
  if (cancelled) target.searchParams.set("cancelled", "1");
  return target.toString();
}

function parseFlyPayReturnIdentifiers(query = {}) {
  const value = key => String((Array.isArray(query[key]) ? query[key][0] : query[key]) ?? "").trim();
  const orderCode = value("orderCode");
  const payOrderCode = value("payOrderCode") || value("merchantOrderNo") || value("payOrderNo") || value("invoice_id") || value("order_no");
  return { orderCode, payOrderCode };
}

function matchesFlyPaySnapshot({ orderCode, payOrderCode }, snapshot = {}) {
  const savedOrderCode = String(snapshot?.orderCode || "").trim();
  const savedPayOrderCode = String(snapshot?.payOrderCode || snapshot?.payOrderNo || "").trim();
  // Never combine a callback with an unrelated checkout left in browser storage.
  return orderCode
    ? orderCode === savedOrderCode && (!payOrderCode || payOrderCode === savedPayOrderCode)
    : !!payOrderCode && payOrderCode === savedPayOrderCode;
}

export function resolveFlyPayReturnSnapshot(query = {}, snapshot = {}) {
  return matchesFlyPaySnapshot(parseFlyPayReturnIdentifiers(query), snapshot) ? snapshot : {};
}

export function resolveFlyPayReturnIdentifiers(query = {}, snapshot = {}) {
  const { orderCode, payOrderCode } = parseFlyPayReturnIdentifiers(query);
  const matches = matchesFlyPaySnapshot({ orderCode, payOrderCode }, snapshot);
  return {
    orderCode: orderCode || (matches ? String(snapshot.orderCode || "").trim() : ""),
    payOrderCode: payOrderCode || (matches ? String(snapshot.payOrderCode || snapshot.payOrderNo || "").trim() : ""),
  };
}

export function buildFlyPayAddress(address = {}) {
  const text = (...values) => values.map(value => String(value ?? "").trim()).find(Boolean) || "";
  const addressLine = text(address.address_line, address.address_line1, address.addressLine1);
  return {
    first_name: text(address.first_name),
    last_name: text(address.last_name, address.first_name),
    country: text(address.country, "AU").toUpperCase(),
    state: text(address.state),
    city: text(address.city),
    address_line: addressLine,
    // FlyPay requires this even when the shopper has a single address line.
    // Reuse the supplied complete address instead of inventing address details.
    address_line_2: text(address.address_line_2, address.address_line2, address.addressLine2, addressLine),
    zipcode: text(address.zipcode, address.postal_code, address.zipCode, address.postalCode),
    email: text(address.email),
    phone: text(address.phone),
  };
}

export function buildFlyPayPreparePayload({ order, comboOrder, origin, apiEnvironment, returnRouting, method = {}, terminal = "PC", shippingInfo = {}, billingInfo = shippingInfo, shopperReference = "" }) {
  if (!order?.dealerShopId || !order.customerAddressDTO || !order.createOrderSkuReqDTOList?.length) {
    throw new Error("Missing FlyPay checkout order parameters");
  }
  if (typeof order.dealerShopId === "number" && !Number.isSafeInteger(order.dealerShopId)) {
    throw new Error("Invalid FlyPay shop ID");
  }
  const rows = order.createOrderSkuReqDTOList.map(item => ({ ...item, sendTypes: "0" }));
  const shipping = buildFlyPayAddress(shippingInfo);
  const routing = resolveFlyPayReturnRouting(returnRouting);
  return {
    order: {
      ...order,
      ...(usesFlyPayProductionOrigin(origin, apiEnvironment, routing)
        ? { domainName: `${routing.callbackOrigin}/` }
        : {}),
      dealerShopId: String(order.dealerShopId), countryCode: "AU", createOrderSkuReqDTOList: rows,
    },
    comboOrder: comboOrder ?? rows.some(item => Number(item.itemType) === 2 || !!item.comboCode),
    terminal,
    ...buildFlyPayReturnUrls({ origin, apiEnvironment, returnRouting }),
    ...(method.paymentMethodCode ? { paymentMethodCode: method.paymentMethodCode } : {}),
    shippingInfo: shipping,
    billingInfo: buildFlyPayAddress(billingInfo),
    ...(shopperReference ? { shopperReference: String(shopperReference) } : {}),
    channelParams: { buyer_email: shipping.email, phone: shipping.phone, country: shipping.country },
  };
}

export function buildFlyPayPaymentPayload({
  orderCode, dealerShopId, payOrderCode = "", origin, apiEnvironment = "test", returnRouting, method = {}, terminal = "PC",
  shippingInfo = {}, billingInfo = shippingInfo, items = [], shopperReference = "",
}) {
  if (!String(orderCode || "").trim()) throw new Error("Missing FlyPay order code");
  if (!dealerShopId || (typeof dealerShopId === "number" && !Number.isSafeInteger(dealerShopId))) {
    throw new Error("Missing or invalid FlyPay shop ID");
  }
  const shipping = buildFlyPayAddress(shippingInfo);
  const payload = {
    orderCode: String(orderCode),
    payType: FLYPAY_PAY_TYPE,
    dealerShopId: String(dealerShopId),
    terminal,
    ...buildFlyPayReturnUrls({ origin, apiEnvironment, orderCode, payOrderCode, returnRouting }),
    shipping_info: shipping,
    billing_info: buildFlyPayAddress(billingInfo),
  };
  if (method.paymentMethodCode) payload.paymentMethodCode = method.paymentMethodCode;
  if (shopperReference) payload.shopper_reference = String(shopperReference);
  if (items.length) {
    payload.items = items.map((item) => {
      const quantity = Math.max(1, Number(item.quantity || item.qty || item.buyAmount || 1));
      const lineAmount = Number(item.realSubtotalAmount ?? item.amount ?? item.price ?? 0);
      return {
        name: item.name || item.title || item.dealerItemSpuTitle || "Item",
        price: lineAmount / quantity,
        item_num: quantity,
        unit: "item",
        ...(item.url ? { url: item.url } : {}),
        remark: String(orderCode),
      };
    });
  }
  payload.channel_params = {
    buyer_email: shipping.email,
    phone: shipping.phone,
    country: shipping.country,
  };
  return payload;
}

export function resolveFlyPayRedirectUrl(response = {}) {
  if (response.success === false || (response.code && String(response.code) !== "200")) {
    throw new Error(response.msg || response.message || "Failed to create FlyPay payment");
  }
  const redirectUrl = String(response.data?.redirectUrl || "").trim();
  if (!/^https?:\/\//i.test(redirectUrl)) {
    throw new Error(response.data?.failReason || "FlyPay did not return a checkout URL");
  }
  return new URL(redirectUrl).toString();
}

// Match the requested order exactly; an unrelated order must never confirm payment.
export function findFlyPayOrder(response = {}, orderCode = "", payOrderCode = "") {
  if (response.success === false) return null;
  const rows = [];
  const visit = (value) => {
    if (Array.isArray(value)) return value.forEach(visit);
    if (!value || typeof value !== "object") return;
    if (value.orderState !== null && value.orderState !== undefined) rows.push(value);
    for (const key of ["data", "dataList", "list", "records", "orderCodeGroupList", "orderList", "mallOrderDTOList", "dealerOrderDTOList", "orderBackDTOList", "orderInfo", "order", "mallOrderDTO", "dealerOrderDTO"]) {
      if (value[key]) visit(value[key]);
    }
  };
  visit(response.data);
  return orderCode
    ? rows.find((order) => String(order.orderCode || "") === String(orderCode)) || null
    : rows.find((order) => payOrderCode && String(order.payOrderCode || order.payOrderNo || "") === String(payOrderCode)) || null;
}

export function getFlyPayOrderPaymentState(order) {
  if (!order || order.orderState === null || order.orderState === undefined || order.orderState === "") return "pending";
  const state = Number(order.orderState);
  if ([1, 2, 3, 4, 5].includes(state)) return "paid";
  if (state === 6) return "refunded";
  if (state === 7) return "cancelled";
  return "pending";
}
